/**
 * Shared donation amount / expected-total utility.
 *
 * Framework-agnostic plain JS — safe to import from both the donor widget
 * (React) and the CRM modal (React). Mirrors the backend's fee math.
 *
 * Processing fees are ALWAYS applied to a donation — the Stripe cost is
 * unavoidable to Opendate. cover_processing only decides who absorbs it:
 *   - coverProcessing=true  → donor pays amount + fee, organization nets amount.
 *   - coverProcessing=false → donor pays amount, organization nets amount − fee.
 *     The backend deducts the fee from the AbstractOrderItem price so that
 *     Ticket#calculate_costs's inclusive math charges the donor exactly the
 *     entered amount. See Orders::Donation.aoi_price_for.
 *
 * The inclusive-fee formula comes from:
 *   https://support.stripe.com/questions/passing-the-stripe-fee-on-to-customers
 * and is implemented in app/models/fee_rule.rb:
 *   decimal = fee_percentage / 100.0
 *   charge_amount = (amount + fee_cash) / (1 - decimal)
 *   processing_fee = (charge_amount - amount).round(2)
 *
 * ChooseDonation.jsx uses the same field names (processing_fee_percentage,
 * processing_fee_cash, minimum, maximum) to select the applicable fee rule.
 */

/**
 * Parse a potentially currency-formatted string into a float.
 * Mirrors ChooseDonation.jsx's parseFloatFromString.
 *
 * @param {string|number} value - e.g. "$1,234.50" or 50 or "50"
 * @returns {number}
 */
function parseAmount(value) {
  if (typeof value === "number") {
    return value;
  }
  // Strip commas (thousands separators) and any leading $ sign, then parse
  return parseFloat(value.split(",").join("").replace(/^\$/, ""));
}

/**
 * Find the fee rule that covers the given amount.
 * Mirrors ChooseDonation.jsx's feeRules.find() call.
 *
 * @param {number} amount - parsed numeric donation amount
 * @param {Array}  feeRules - array of fee rule objects with minimum/maximum fields
 * @returns {Object|undefined}
 */
function findFeeRule(amount, feeRules) {
  return feeRules.find(function (fr) {
    var min = parseAmount(fr.minimum);
    var max = parseAmount(fr.maximum);
    return amount >= min && amount <= max;
  });
}

/**
 * Round a number to 2 decimal places using the same rounding as Ruby's
 * (amount).round(2) — i.e. Math.round halfway-up.
 *
 * @param {number} value
 * @returns {number}
 */
function round2(value) {
  return Math.round(value * 100) / 100;
}

/**
 * Compute the expected_total that must be sent to the backend to pass the
 * verify_expected_total_matches validation — i.e. the amount the donor's card
 * will actually be charged.
 *
 *   When coverProcessing is false → donor charged exactly amount; org nets amount − fee.
 *   When coverProcessing is true  → donor charged amount + inclusive fee; org nets amount.
 *
 * @param {number}  amount          - parsed donation amount (e.g. 50)
 * @param {boolean} coverProcessing - whether the donor is covering the processing fee
 * @param {Array}   feeRules        - team fee rules (same shape as team.fee_rules JSON)
 * @returns {number} expected_total rounded to 2 decimals
 */
/**
 * Compute the inclusive processing fee for a donation amount — i.e. the extra
 * the donor pays on top of the gift when they choose to cover fees, OR the
 * amount deducted from the gift when they don't. The figure is the same either
 * way (it's always shown to the donor); only who absorbs it changes.
 *
 * Mirrors FeeRule#calculate_inclusive_processing_fee:
 *   decimal       = processing_fee_percentage / 100
 *   charge_amount = (amount + processing_fee_cash) / (1 - decimal)
 *   processing_fee = round2(charge_amount - amount)
 *
 * @param {number} amount   - parsed donation amount (e.g. 10)
 * @param {Array}  feeRules - team fee rules (same shape as team.fee_rules JSON)
 * @returns {number} processing fee rounded to 2 decimals; 0 if no rule matches
 */
function computeProcessingFee(amount, feeRules) {
  var parsedAmount = parseAmount(amount);
  var feeRule = findFeeRule(parsedAmount, feeRules);
  if (!feeRule) {
    // No matching fee rule — no fee we can compute (backend will error on submit)
    return 0;
  }

  var decimal = parseAmount(feeRule.processing_fee_percentage) / 100.0;
  var feeCash = parseAmount(feeRule.processing_fee_cash);
  var chargeAmount = (parsedAmount + feeCash) / (1 - decimal);
  return round2(chargeAmount - parsedAmount);
}

/**
 * The AbstractOrderItem price the backend uses when the donor declines to
 * cover fees: the entered amount minus the EXCLUSIVE processing fee.
 * Mirrors Orders::Donation.aoi_price_for:
 *   deduction = round2(amount * pct/100 + cash)
 *   aoi_price = round2(amount - deduction)
 *
 * @param {number} amount   - parsed donation amount
 * @param {Array}  feeRules - team fee rules
 * @returns {number} AOI price rounded to 2 decimals; amount unchanged if no rule matches
 */
function computeAoiPrice(amount, feeRules) {
  var parsedAmount = parseAmount(amount);
  var feeRule = findFeeRule(parsedAmount, feeRules);
  if (!feeRule) {
    return parsedAmount;
  }

  var pct = parseAmount(feeRule.processing_fee_percentage);
  var cash = parseAmount(feeRule.processing_fee_cash);
  var deduction = round2((parsedAmount * pct) / 100.0 + cash);
  return round2(parsedAmount - deduction);
}

function computeExpectedTotal(amount, coverProcessing, feeRules) {
  var parsedAmount = parseAmount(amount);

  if (coverProcessing) {
    return round2(parsedAmount + computeProcessingFee(parsedAmount, feeRules));
  }

  // cover_processing === false. The backend does NOT charge exactly `amount`:
  // it deducts the exclusive fee to get the AOI price (Orders::Donation.aoi_price_for),
  // then Ticket#calculate_costs adds the INCLUSIVE fee back on that reduced
  // price (final_cost = round2(aoi_price + inclusive_fee(aoi_price))). Those are
  // two independent round(2) ops, so the charged total is usually a penny off
  // `amount` and only equals it by luck (e.g. $50 at 2.9%). Order#verify_expected_total_matches
  // compares with exact equality, so we must reproduce the same two steps here
  // or the donation is rejected with a 422.
  var aoiPrice = computeAoiPrice(parsedAmount, feeRules);
  return round2(aoiPrice + computeProcessingFee(aoiPrice, feeRules));
}

module.exports = {
  parseAmount,
  findFeeRule,
  computeProcessingFee,
  computeAoiPrice,
  computeExpectedTotal,
  MINIMUM_AMOUNT: 1.0,
};
